import type { PropsWithChildren } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { IFrameMessageEventType } from "@/app/providers/auth-provider/types";
import { USER_STORE_SELECTORS, useUserStore } from "@/entities/user";

export function AuthProvider(props: PropsWithChildren) {
  const { t } = useTranslation();
  const user = useUserStore(USER_STORE_SELECTORS.user);
  const authorize = useUserStore(USER_STORE_SELECTORS.authorize);
  const devAuthorize = useUserStore(USER_STORE_SELECTORS.devAuthorize);
  const fetchDevSettings = useUserStore(USER_STORE_SELECTORS.fetchDevSettings);

  const excludedPaths = ["/statistic"];
  const isExcludedPath = excludedPaths.some((path) => window.location.pathname.startsWith(path));

  const applyDevToken = React.useCallback(async () => {
    const settings = await fetchDevSettings();
    if (settings?.devToken) {
      devAuthorize(settings.devToken);
      return true;
    }
    return false;
  }, [devAuthorize, fetchDevSettings]);

  const handleNonIframe = React.useCallback(async () => {
    const applied = await applyDevToken();
    if (applied) {
      return;
    }
    const token = import.meta.env.VITE_PUBLIC_TOKEN;
    if (import.meta.env.DEV && token && typeof token === "string") {
      void authorize(token);
    }
  }, [applyDevToken, authorize]);

  const handleMessage = React.useCallback(
    (e: MessageEvent<IFrameMessageEventType>) => {
      if (e.data?.type === "TOKEN" && e.data?.token) {
        void authorize(e.data.token);
      }
    },
    [authorize],
  );

  React.useLayoutEffect(() => {
    if (isExcludedPath) {
      return;
    }
    if (window.self !== window.top) {
      window.addEventListener("message", handleMessage);
      window.parent.postMessage({ type: "REQUEST_TOKEN" }, "*");
    } else {
      void handleNonIframe();
    }
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [applyDevToken, handleMessage, handleNonIframe, isExcludedPath]);

  if (isExcludedPath) {
    return props.children;
  }

  if (!user) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div>
          {t("common.error.unauthorize.message", { defaultValue: "Не удалось авторизоваться" })}
        </div>
      </div>
    );
  }

  return props.children;
}
